import { useAnalytics } from "@/analytics/AnalyticsContext";
import { VISITOR_EVENTS } from "@/analytics/events";
import CheckCircle from "@/assets/CheckCirle.png";
import { useWizardProgress } from "@/hooks/useWizardProgress";
import { RootState } from "@/store";
import { updateEnrolledStatus } from "@/store/userSlice";
import { setCurrentStep } from "@/store/wizard";
import { EmployeeState } from "@/types/employeeState";
import { getDecodedTokenSafe } from "@/utils/tokenUtils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../Common/Buttons/Button";

interface UserBenefitData {
  EmployerMaxContribution: string;
  VestingScheduleName: string;
  BenefitContributionLimit: string;
  BenefitPlanStartDate: string;
}

const SetupComplete: React.FC<{
  isUserEnrolled?: boolean;
}> = ({ isUserEnrolled }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const user = useSelector((state: RootState) => state.user);
  const [userData, setUserData] = useState<UserBenefitData>({
    EmployerMaxContribution: "",
    VestingScheduleName: "",
    BenefitContributionLimit: "",
    BenefitPlanStartDate: ``,
  });

  const { clearProgress } = useWizardProgress();

  useEffect(() => {
    const fetchUserBenefits = () => {
      const decodedToken = getDecodedTokenSafe<UserBenefitData>(() => {});

      if (decodedToken) {
        setUserData({
          EmployerMaxContribution: decodedToken.EmployerMaxContribution,
          VestingScheduleName: decodedToken.VestingScheduleName,
          BenefitContributionLimit: decodedToken.BenefitContributionLimit,
          BenefitPlanStartDate: decodedToken.BenefitPlanStartDate,
        });
      }
    };

    fetchUserBenefits();
  }, []);

  const handleLeaveSetup = () => {
    // Track onboarding completion
    trackEvent(VISITOR_EVENTS.USER_COMPLETES_ONBOARDING, {
      user_id: user.id,
      username: user.username,
    });

    dispatch(setCurrentStep(1));
    clearProgress();
    dispatch(updateEnrolledStatus(EmployeeState.Accepted));
    navigate("/");
  };

  return (
    <>
      {isUserEnrolled ? (
        <div className="w-[640px] mx-auto bg-white font-outfit">
          <div className="flex flex-col">
            <img
              src={CheckCircle}
              alt="Check mark"
              className="w-16 h-16 mb-4"
            />
            <h2 className="text-2xl font-bold mb-4">
              {t("onboarding.setup_complete.beneficiary_added_title")}
            </h2>
            <p className="text-black mb-6">
              {t("onboarding.setup_complete.beneficiary_added_description")}
            </p>
            <p className="text-black font-semibold mb-2">
              {t("onboarding.setup_complete.whats_next")}
            </p>

            <ul className="mb-6">
              <li className="list-disc ml-6">
                {t("onboarding.setup_complete.track_progress")}
              </li>
              <li className="list-disc ml-6">
                {t("onboarding.setup_complete.contributions_grow")}
              </li>
            </ul>

            <p className="text-black  mb-[40px]">
              {t("onboarding.setup_complete.thank_you_message")}
            </p>

            <button
              onClick={handleLeaveSetup}
              className="bg-white text-black font-semibold py-4 px-4 border border-gray-300 hover:bg-gray-50 transition duration-200 rounded-md w-40"
            >
              {t("onboarding.setup_complete.leave_setup_button")}
            </button>
          </div>
        </div>
      ) : (
        <div className="w-[640px] mx-auto bg-white p-[32px] rounded-[16px] shadow-introBox font-outfit mt-10">
          <div className="flex flex-col">
            <img
              src={CheckCircle}
              alt="Check mark"
              className="w-16 h-16 mb-4"
            />
            <h2 className="text-2xl font-bold mb-4">
              {t("onboarding.setup_complete.setup_complete_title")}
            </h2>
            <p className="text-black mb-6">
              {t("onboarding.setup_complete.setup_complete_description", {
                startDate: userData.BenefitPlanStartDate.split(" ")[0],
              })}
            </p>
            <p className="text-black font-semibold mb-2">
              {t("onboarding.setup_complete.whats_next")}
            </p>

            <ul className="mb-6">
              <li className="list-disc ml-6">
                {t("onboarding.setup_complete.track_progress")}
              </li>
              <li className="list-disc ml-6">
                {t("onboarding.setup_complete.contributions_grow")}
              </li>
            </ul>

            <p className="text-black  mb-[40px]">
              {t("onboarding.setup_complete.thank_you_message")}
            </p>

            {/* <button
              onClick={handleLeaveSetup}
              className="flex space-x-2 items-center shadow-backBtn p-4 border-[1px] border-[#E2E2E2] bg-white text-black rounded-[8px]"
              //className="bg-white text-black font-semibold py-4 px-4 border border-gray-300 hover:bg-gray-50 transition duration-200 rounded-md w-40"
            >
              Leave Set-up
            </button> */}
            <Button
              iconVisibility="hide"
              ariaLabel="next"
              variant="secondary"
              label={t("onboarding.setup_complete.leave_setup_button")}
              loading={false}
              handleClick={handleLeaveSetup}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SetupComplete;
